import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const HeroSection = () => {
  const [scrollY, setScrollY] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll, { passive: true });
    setTimeout(() => setLoaded(true), 100);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <section className="relative min-h-screen overflow-hidden flex items-center" style={{ background: "#0a1628" }}>
      {/* Multi-layer animated background */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(135deg, #0a1628 0%, #0d1f3c 40%, #0a1628 100%)" }} />

        <div
          className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full opacity-20"
          style={{
            background: "radial-gradient(circle, #ffd700 0%, transparent 70%)",
            filter: "blur(80px)",
            transform: `translateY(${scrollY * 0.05}px)`
          }} />

        <div
          className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full opacity-15"
          style={{
            background: "radial-gradient(circle, #1a4a8a 0%, transparent 70%)",
            filter: "blur(80px)",
            transform: `translateY(${scrollY * -0.03}px)`
          }} />

        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: "linear-gradient(rgba(255,215,0,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,215,0,1) 1px, transparent 1px)",
            backgroundSize: "80px 80px"
          }} />

        <div
          className="absolute top-0 right-0 w-1/2 h-full opacity-10"
          style={{ background: "linear-gradient(135deg, transparent 0%, rgba(255,215,0,0.15) 50%, transparent 100%)" }} />

      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16 w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left content */}
          <div
            style={{
              opacity: loaded ? 1 : 0,
              transform: loaded ? "translateY(0)" : "translateY(30px)",
              transition: "opacity 0.8s ease, transform 0.8s ease"
            }}>

            {/* Badge */}
            <div className="inline-flex items-center gap-2.5 mb-8 px-4 py-2 rounded-full border"
            style={{ background: "rgba(255,215,0,0.08)", borderColor: "rgba(255,215,0,0.25)" }}>

              <span className="w-2 h-2 rounded-full bg-[#ffd700] animate-pulse" />
              <span className="text-[#ffd700] text-sm font-semibold tracking-wide">Africa's #1 Smart Mobility Platform</span>
            </div>

            {/* Headline */}
            <h1
              className="font-black text-white leading-[1.02] mb-6"
              style={{ fontSize: "clamp(2.8rem, 6vw, 5rem)", fontFamily: "'DM Sans', sans-serif" }}>

              Move Smart.
              <br />
              <span
                style={{
                  background: "linear-gradient(90deg, #ffd700 0%, #f59e0b 50%, #ffd700 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text"
                }}>

                Deliver Fast.
              </span>
              <br />
              <span style={{ color: "rgba(255,255,255,0.75)" }}>Earn More.</span>
            </h1>

            <p className="text-white/55 text-lg lg:text-xl leading-relaxed mb-10 max-w-lg">OnTheGo connects riders, drivers, and businesses across Africa with rides, parcels, food delivery, and fleet investment in one powerful platform.

            </p>

            {/* CTA buttons */}
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => navigate("/customer-login-screen")}
                className="inline-flex items-center gap-3 px-8 py-4 rounded-2xl font-bold text-[#0a1628] transition-all duration-300 hover:scale-105 hover:shadow-2xl"
                style={{
                  background: "linear-gradient(135deg, #ffd700 0%, #f59e0b 100%)",
                  boxShadow: "0 8px 32px rgba(255,215,0,0.3)"
                }}>

                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Book a Ride or Delivery
              </button>
              <a
                href="#download"
                onClick={(e) => {e?.preventDefault();document.querySelector("#download")?.scrollIntoView({ behavior: "smooth" });}}
                className="inline-flex items-center gap-3 px-8 py-4 rounded-2xl font-bold text-white border transition-all duration-300 hover:scale-105"
                style={{
                  background: "rgba(255,255,255,0.06)",
                  borderColor: "rgba(255,255,255,0.2)",
                  backdropFilter: "blur(10px)"
                }}>

                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.7 9.05 7.4c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.39-1.32 2.76-2.53 3.99zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                </svg>
                Download the App
              </a>
            </div>
          </div>

          {/* Right visual */}
          <div
            className="relative hidden lg:flex items-center justify-center"
            style={{
              opacity: loaded ? 1 : 0,
              transform: loaded ? "translateY(0)" : "translateY(20px)",
              transition: "opacity 1s ease 0.3s, transform 1s ease 0.3s"
            }}>

            {/* Glow ring */}
            <div
              className="absolute w-80 h-80 rounded-full opacity-20"
              style={{
                background: "radial-gradient(circle, #ffd700 0%, transparent 70%)",
                filter: "blur(40px)",
                transform: `translateY(${scrollY * -0.06}px)`
              }} />


            {/* Phone mockup */}
            <div
              className="relative z-10"
              style={{ transform: `translateY(${scrollY * -0.08}px)` }}>

              <div
                className="w-60 h-[500px] rounded-[3rem] border-2 overflow-hidden shadow-2xl"
                style={{
                  background: "linear-gradient(180deg, #0d1f3c 0%, #0a1628 100%)",
                  borderColor: "rgba(255,215,0,0.2)",
                  boxShadow: "0 40px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,215,0,0.1), inset 0 1px 0 rgba(255,255,255,0.05)"
                }}>

                {/* Notch */}
                <div className="flex justify-center pt-3 pb-2">
                  <div className="w-24 h-5 bg-black rounded-full" />
                </div>
                {/* App UI */}
                <div className="px-5 py-3">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-white/40 text-xs">Good morning,</div>
                      <div className="text-white font-black text-sm">Amara 👋</div>
                    </div>
                    <div
                      className="w-8 h-8 rounded-xl flex items-center justify-center font-black text-[#0a1628] text-xs"
                      style={{ background: "linear-gradient(135deg, #ffd700, #f59e0b)" }}>

                      OG
                    </div>
                  </div>

                  {/* Map */}
                  <div
                    className="rounded-2xl h-36 mb-4 relative overflow-hidden"
                    style={{ background: "linear-gradient(135deg, #1a365d 0%, #0d2a4a 100%)" }}>

                    <div
                      className="absolute inset-0 opacity-15"
                      style={{
                        backgroundImage: "linear-gradient(rgba(255,215,0,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,215,0,0.6) 1px, transparent 1px)",
                        backgroundSize: "18px 18px"
                      }} />

                    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 240 144">
                      <path d="M 40 110 Q 80 60 140 50 Q 180 42 200 30" stroke="#ffd700" strokeWidth="2" fill="none" strokeDasharray="4 3" opacity="0.6" />
                      <circle cx="40" cy="110" r="5" fill="#ffd700" />
                      <circle cx="200" cy="30" r="5" fill="#34d399" />
                    </svg>
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                      <div className="w-4 h-4 bg-[#ffd700] rounded-full" style={{ boxShadow: "0 0 12px rgba(255,215,0,0.8)" }} />
                      <div className="w-4 h-4 bg-[#ffd700] rounded-full animate-ping absolute top-0 left-0 opacity-50" />
                    </div>
                  </div>

                  {/* Booking card */}
                  <div
                    className="rounded-2xl p-3 mb-3 border"
                    style={{ background: "rgba(255,215,0,0.06)", borderColor: "rgba(255,215,0,0.15)" }}>

                    <div className="text-white/40 text-xs mb-1.5">Where to?</div>
                    <div className="text-white font-bold text-sm mb-1">📍 Westlands, Nairobi</div>
                    <div className="flex items-center justify-between">
                      <div className="text-white/40 text-xs">Boda Boda • 3 min away</div>
                      <div className="text-[#ffd700] text-xs font-bold">KES 180</div>
                    </div>
                  </div>

                  <div
                    className="w-full py-3 rounded-xl text-center font-bold text-sm text-[#0a1628]"
                    style={{ background: "linear-gradient(135deg, #ffd700, #f59e0b)" }}>

                    Confirm Booking →
                  </div>
                </div>
              </div>
            </div>

            {/* Floating card 1: Rider Nearby */}
            <div
              className="absolute top-12 -left-12 rounded-2xl p-4 border shadow-xl"
              style={{
                background: "rgba(13,31,60,0.95)",
                borderColor: "rgba(255,215,0,0.2)",
                backdropFilter: "blur(20px)",
                animation: "heroFloat 3.5s ease-in-out infinite"
              }}>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl" style={{ background: "rgba(255,215,0,0.15)" }}>🛵</div>
                <div>
                  <div className="text-white text-xs font-bold">Rider Nearby</div>
                  <div className="text-[#ffd700] text-xs">2 min away</div>
                </div>
              </div>
            </div>

            {/* Floating card 2: Today's Earnings */}
            <div
              className="absolute top-1/3 -right-14 rounded-2xl p-4 shadow-xl"
              style={{
                background: "linear-gradient(135deg, #ffd700, #f59e0b)",
                animation: "heroFloat 3.5s ease-in-out infinite 0.6s"
              }}>

              <div className="text-[#0a1628] text-xs font-bold mb-0.5">Today's Earnings</div>
              <div className="text-[#0a1628] text-xl font-black">KES 4,850</div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/25">
        <span className="text-xs tracking-[0.2em] uppercase font-medium">Scroll</span>
        <div className="w-px h-10 bg-gradient-to-b from-white/25 to-transparent" />
      </div>
      <style>{`
        @keyframes heroFloat {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-14px); }
        }
      `}</style>
    </section>);

};

export default HeroSection;